'use client';

import Link from '~/(tenant)/_components/tenant-link';

import { useQuery } from '@tanstack/react-query';
import { ArrowUpRight, UserPlus } from 'lucide-react';

import { useSupabase } from '@kit/supabase/hooks/use-supabase';
import { Trans } from '@kit/ui/trans';

import { Capabilities } from '@kit/capabilities';
import { useCapabilityOptional } from '@kit/capabilities/client';

import { useNativeTokens } from '~/(tenant)/_components/native/_lib/use-native-tokens';
import { SectionHeader } from '~/(tenant)/_components/native/_primitives';

interface GuestAllowanceSectionProps {
  accountId: string;
}

interface GuestPolicyBucket {
  adults?: number | null;
  kids?: number | null;
}

interface GuestPolicy {
  weekday?: GuestPolicyBucket;
  weekend?: GuestPolicyBucket;
  daily?: { weekday?: GuestPolicyBucket; weekend?: GuestPolicyBucket };
  monthly?: GuestPolicyBucket;
}

interface MembershipRow {
  id: string;
  membership_plans: { guest_policy: GuestPolicy | null } | null;
}

interface AllowanceData {
  membershipId: string;
  remaining: number;
  allowed: number;
}

type SupabaseLike = {
  from: (table: string) => UntypedQuery;
};
type UntypedQuery = {
  select: (
    columns: string,
    options?: { count?: 'exact'; head?: boolean },
  ) => UntypedQuery;
  eq: (col: string, val: unknown) => UntypedQuery;
  in: (col: string, vals: readonly unknown[]) => UntypedQuery;
  maybeSingle: <T = unknown>() => Promise<{ data: T | null }>;
} & PromiseLike<{ count: number | null }>;

/**
 * Per-tier daily guest allowance card. Hidden when:
 *   - tenant has no GUEST_PASSES capability
 *   - user is not signed in
 *   - user has no active membership
 *   - the user's tier has an empty guest_policy (e.g. Oia EOS, Silver Sands External)
 */
export function GuestAllowanceSection({
  accountId,
}: GuestAllowanceSectionProps) {
  const enabled = useCapabilityOptional(Capabilities.GUEST_PASSES);
  const client = useSupabase();

  const { data: session } = useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      const { data } = await client.auth.getSession();
      return data.session;
    },
    staleTime: 60_000,
    enabled,
  });

  const userId = session?.user?.id;

  const { data: allowance } = useQuery({
    queryKey: ['guest-allowance', accountId, userId],
    enabled: enabled && Boolean(userId),
    staleTime: 60_000,
    queryFn: async (): Promise<AllowanceData | null> => {
      if (!userId) return null;

      // TODO(member-club-phase-1): cast to SupabaseLike until the
      // 51-guest-passes.sql migration runs and types regenerate. Once
      // `guest_policy` and `guest_passes` exist on the generated
      // schema, swap to the typed `client.from(...)`.
      const c = client as unknown as SupabaseLike;

      const today = new Date();
      const isWeekend = today.getDay() === 5 || today.getDay() === 6; // FRI / SAT
      const todayIso = today.toISOString().split('T')[0]!;

      const membershipResult = await c
        .from('user_memberships')
        .select('id, membership_plans:plan_id(guest_policy)')
        .eq('user_id', userId)
        .eq('account_id', accountId)
        .eq('status', 'active')
        .eq('is_deleted', false)
        .maybeSingle<MembershipRow>();

      const membership = membershipResult.data;
      if (!membership) return null;

      const policy = membership.membership_plans?.guest_policy ?? null;
      if (!policy || Object.keys(policy).length === 0) {
        // Tier doesn't allow guests at all.
        return null;
      }

      if ('monthly' in policy) return null;

      const daily = 'daily' in policy ? policy.daily : policy;
      const bucket = isWeekend ? daily?.weekend : daily?.weekday;
      const allowed = (bucket?.adults ?? 0) + (bucket?.kids ?? 0);

      if (allowed <= 0) {
        // Bucket present but zero — same effect as no allowance today.
        return null;
      }

      const passesResult = await c
        .from('guest_passes')
        .select('id', { count: 'exact', head: true })
        .eq('invited_by_membership_id', membership.id)
        .eq('valid_for_date', todayIso)
        .eq('is_deleted', false)
        .in('status', ['pending_payment', 'active', 'used']);

      const used = passesResult.count ?? 0;
      const remaining = Math.max(0, allowed - used);

      return {
        membershipId: membership.id,
        remaining,
        allowed,
      };
    },
  });

  if (!enabled) return null;
  if (!userId || !allowance) return null;

  return <GuestAllowanceCard allowance={allowance} />;
}

function GuestAllowanceCard({ allowance }: { allowance: AllowanceData }) {
  const t = useNativeTokens();

  return (
    <section style={{ paddingTop: 32 }}>
      <SectionHeader
        eyebrow={
          <Trans
            i18nKey="common:nativeHome.guestAllowance.eyebrow"
            defaults="Today"
          />
        }
        title={
          <Trans
            i18nKey="common:nativeHome.guestAllowance.title"
            defaults="Guest allowance"
          />
        }
      />

      <div style={{ marginTop: 18, padding: '0 22px' }}>
        <Link
          href="/guests/new"
          data-test="guest-allowance-cta"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
            padding: 18,
            borderRadius: t.radius,
            border: `1px solid ${t.line}`,
            backgroundColor: t.surface,
            color: t.foreground,
            textDecoration: 'none',
          }}
        >
          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 999,
                border: `1px solid ${t.line}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: t.foreground,
                flexShrink: 0,
              }}
              aria-hidden
            >
              <UserPlus size={20} strokeWidth={1.4} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontFamily: t.fontDisplay,
                  fontSize: 19,
                  fontWeight: 400,
                  lineHeight: 1.2,
                  color: t.foreground,
                }}
              >
                <Trans
                  i18nKey="common:nativeHome.guestAllowance.remaining"
                  values={{
                    remaining: allowance.remaining,
                    allowed: allowance.allowed,
                  }}
                  defaults="{{remaining}} of {{allowed}} guests left today"
                />
              </div>
              <div
                style={{
                  fontFamily: t.fontMono,
                  fontSize: 9,
                  letterSpacing: '0.16em',
                  textTransform: 'uppercase',
                  color: t.muted,
                  marginTop: 6,
                }}
              >
                <Trans
                  i18nKey="common:nativeHome.guestAllowance.cta"
                  defaults="Invite a guest"
                />
              </div>
            </div>
          </div>

          <ArrowUpRight
            size={16}
            strokeWidth={1.4}
            color={t.muted}
            aria-hidden
          />
        </Link>
      </div>
    </section>
  );
}
